import { Mesh, Group, Object3D, ShapeGeometry } from "three";
import { SPEECH_BUBBLE_ANCHOR_POINT_OFFSET, createSpeechBubbleArrowGeometry, roundedRect } from "./speechBubbleGeometry";
import { degToRad, TooltipPlacement, getColorBrightness } from "flavr-common";
import { Vector3, Vector2 } from "flavr-math";
import { RenderOrder } from "../RenderOrder";
import { SPEECH_BUBBLE_OVERLAY_MATERIALS, SPEECH_BUBBLE_INNER_COLOR, SPEECH_BUBBLE_BORDER_COLOR } from "./speechBubbleMaterials";
import { Text } from "troika-three-text";
import { camera } from "../camera";
import { modelPool } from "@modules/model";
const TEXT_SCALE = new Vector3().setScalar(1);
const MIN_WIDTH = 0.4;
const AVATAR_SCALE_FACTOR = 0.15;
const AVATAR_HEIGHT = 0.25;
export var SpeechBubbleTextAlingment;
(function (SpeechBubbleTextAlingment) {
    SpeechBubbleTextAlingment[SpeechBubbleTextAlingment["Left"] = -1] = "Left";
    SpeechBubbleTextAlingment[SpeechBubbleTextAlingment["Center"] = 0] = "Center";
    SpeechBubbleTextAlingment[SpeechBubbleTextAlingment["Right"] = 1] = "Right";
})(SpeechBubbleTextAlingment || (SpeechBubbleTextAlingment = {}));
export class SpeechBubble {
    constructor(renderer, modelUID) {
        var _a;
        this.textColor = 0xffffff;
        this.align = SpeechBubbleTextAlingment.Left;
        this.toolTipPlacement = TooltipPlacement.RIGHT_OF_MODEL;
        this.hasAvatar = false;
        this.overlayMaterials = SPEECH_BUBBLE_OVERLAY_MATERIALS.map((mat) => mat.clone());
        this.background = new Mesh();
        this.overlay = new Mesh();
        this.group = new Group();
        this.group.matrixAutoUpdate = false;
        this.group.frustumCulled = true;
        const parentRoom = (_a = modelPool.getModel(modelUID)) === null || _a === void 0 ? void 0 : _a.getRoom();
        if (parentRoom !== undefined) {
            this.root = renderer.getRootNodeForRoom(parentRoom.getUID());
        }
        else {
            this.root = new Object3D();
        }
        this.root.add(this.group);
        this.setVisible(false);
    }
    createTextInstance() {
        if (this.text)
            return;
        this.text = new Text();
        this.text.gpuAccelerateSDF = true;
        this.text.renderOrder = RenderOrder.TOOLTIP_TEXT;
        this.text.textAlign = "center";
        this.text.anchorX = "center";
        this.text.anchorY = "middle";
        this.text.translateZ(-0.01);
        this.text.color = this.textColor;
        this.group.add(this.text);
        // this gets called everytime the text is updated (text.sync())
        this.text.sync(() => {
            this.onTextSync();
        });
    }
    destroyTextInstance() {
        if (this.text) {
            this.group.remove(this.text);
            this.text = undefined;
        }
    }
    setColor(color = SPEECH_BUBBLE_INNER_COLOR) {
        this.overlayMaterials[0].color.setHex(color);
        // pick text color based on background brightness
        const brightness = getColorBrightness(this.overlayMaterials[0].color);
        if (brightness > 0.6) {
            this.textColor = 0x000000;
        }
        else {
            this.textColor = 0xffffff;
        }
    }
    setBorderColor(color = SPEECH_BUBBLE_BORDER_COLOR) {
        this.overlayMaterials[1].color.setHex(color);
    }
    setHasAvatar(hasAvatar) {
        this.hasAvatar = hasAvatar;
    }
    setTextAlingment(align) {
        this.align = align;
    }
    setTooltipPlacement(placement) {
        this.toolTipPlacement = placement;
    }
    setVisible(visible) {
        if (this.text !== undefined)
            this.text.visible = visible;
        this.group.visible = visible;
        if (!visible && this.arrow !== undefined)
            this.removeArrowMesh();
    }
    setText(text) {
        if (!this.text)
            return;
        this.destroyTextInstance();
        this.createTextInstance();
        this.text.text = text;
        this.text.textAlign = this.align === SpeechBubbleTextAlingment.Left ? "left" : this.align === SpeechBubbleTextAlingment.Right ? "right" : "center";
        if (!Array.isArray(this.text.material))
            this.text.material.depthTest = false;
        this.text.scale.copy(TEXT_SCALE);
        this.text.rotateY(degToRad(180));
        this.text.sync(() => {
            this.onTextSync();
        });
    }
    removeAvatar() {
        if (this.avatar) {
            this.group.remove(this.avatar);
            this.avatar = undefined;
        }
    }
    setAvatar(avatar) {
        this.removeAvatar();
        this.avatar = avatar.clone();
        this.avatar.castShadow = false;
        this.avatar.traverse((object) => {
            object.matrixAutoUpdate = false;
            object.frustumCulled = false;
            object.receiveShadow = false;
            if (object instanceof Mesh) {
                // clone materials and set dephTest to false
                if (object.material instanceof Array) {
                    object.material = object.material.map((mat) => mat.clone());
                    for (const material of object.material) {
                        material.depthTest = false;
                        material.transparent = true;
                    }
                }
                else {
                    object.material = object.material.clone();
                    object.material.depthTest = false;
                    object.material.transparent = true;
                }
            }
            object.renderOrder = RenderOrder.TOOLTIP_TEXT;
        });
        this.group.add(this.avatar);
    }
    updateAvatarPosition() {
        if (!this.avatar || !this.text)
            return;
        this.avatar.position.set(this.getHorizontalOffset(), SPEECH_BUBBLE_ANCHOR_POINT_OFFSET.y + TEXT_SCALE.y * this.getHeight() + 0.5 * AVATAR_HEIGHT, -SPEECH_BUBBLE_ANCHOR_POINT_OFFSET.z);
        this.avatar.scale.setScalar(AVATAR_SCALE_FACTOR);
        this.avatar.rotateY(degToRad(180));
        this.avatar.rotateX(degToRad(-90));
        this.avatar.updateMatrix();
    }
    destroy() {
        var _a;
        this.destroyTextInstance();
        (_a = this.group.parent) === null || _a === void 0 ? void 0 : _a.remove(this.group);
    }
    setParent(parent) {
        parent.add(this.group);
    }
    getHorizontalOffset() {
        if (this.toolTipPlacement > 2)
            return 0;
        return this.toolTipPlacement * (0.5 * this.getWidth() + SPEECH_BUBBLE_ANCHOR_POINT_OFFSET.x);
    }
    getVerticalOffset() {
        return 0.5 * this.getHeight() + SPEECH_BUBBLE_ANCHOR_POINT_OFFSET.y;
    }
    removeArrowMesh() {
        var _a, _b, _c;
        if (this.arrow !== undefined)
            this.root.remove(this.arrow);
        (_a = this.arrow) === null || _a === void 0 ? void 0 : _a.geometry.dispose();
        if (!Array.isArray((_b = this.arrow) === null || _b === void 0 ? void 0 : _b.material))
            (_c = this.arrow) === null || _c === void 0 ? void 0 : _c.material.dispose();
        this.arrow = undefined;
    }
    setTransform(pos, ori, scale) {
        this.group.position.copy(pos);
        this.group.quaternion.copy(ori);
        this.group.scale.setScalar(scale).multiplyScalar(0.35);
        const cameraWorldDirection = camera.perspectiveCam.getWorldDirection(new Vector3());
        const cameraWorldUp = camera.perspectiveCam.up.clone();
        const cameraWorldRight = cameraWorldDirection.cross(cameraWorldUp).normalize();
        switch (this.toolTipPlacement) {
            case TooltipPlacement.LEFT_OF_MODEL:
                this.group.position.sub(cameraWorldRight.multiplyScalar(scale / 2));
                break;
            case TooltipPlacement.RIGHT_OF_MODEL:
                this.group.position.add(cameraWorldRight.multiplyScalar(scale / 2));
                break;
            case TooltipPlacement.ABOVE_MODEL:
                this.group.position.add(cameraWorldUp.multiplyScalar(scale / 4));
                break;
            case TooltipPlacement.BELOW_MODEL:
                this.group.position.sub(cameraWorldUp.multiplyScalar(scale / 4));
                break;
            default:
                break;
        }
        this.group.updateMatrix();
        // handle arrow
        this.removeArrowMesh();
        // const tooltipOrigin = this.group.getWorldPosition(new Vector3()).add(camera.perspectiveCam.getWorldDirection(new Vector3()).multiplyScalar(0.001));
        const tooltipOrigin = this.group.getWorldPosition(new Vector3());
        this.arrow = new Mesh(createSpeechBubbleArrowGeometry(pos, tooltipOrigin, new Vector2(this.width, this.height), scale), this.overlayMaterials[1]);
        this.root.add(this.arrow);
    }
    getWidth() {
        return Math.max(MIN_WIDTH, TEXT_SCALE.x * (this.text && this.width ? this.width : 1));
    }
    getHeight() {
        return TEXT_SCALE.y * (this.text && this.height ? this.height : 1) + (this.hasAvatar ? AVATAR_HEIGHT : 0);
    }
    onTextSync() {
        var _a;
        if (!this.text)
            return;
        const boundingBoxSize = new Vector3();
        (_a = this.text.geometry.boundingBox) === null || _a === void 0 ? void 0 : _a.getSize(boundingBoxSize);
        this.text.color = this.textColor;
        this.width = boundingBoxSize.x + 0.1;
        this.height = boundingBoxSize.y + 0.1;
        if (this.background.geometry)
            this.background.geometry.dispose();
        if (this.overlay.geometry)
            this.overlay.geometry.dispose();
        this.background.geometry = new ShapeGeometry(roundedRect(-this.width / 2, -this.height / 2, this.width, this.height, 0.1));
        this.background.material = this.overlayMaterials[0];
        this.background.renderOrder = RenderOrder.TOOLTIP_TEXT;
        this.overlay.geometry = new ShapeGeometry(roundedRect(-this.width / 2 - 0.025, -this.height / 2 - 0.025, this.width + 0.05, this.height + 0.05, 0.12));
        this.overlay.material = this.overlayMaterials[1];
        this.overlay.renderOrder = RenderOrder.TOOLTIP_TEXT;
        this.overlay.position.z = 0.01;
        this.group.add(this.background);
        this.group.add(this.overlay);
    }
}
